import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Package,
  ClipboardCheck,
  ClipboardList,
  FileText,
  Building2,
  Users,
  Settings,
  HelpCircle,
  Truck,
  Cpu,
  Lock,
  Network,
  ShieldCheck,
  ListTodo,
  Target,
  BarChart3,
  Link2,
  Calendar,
  Award,
  FileBarChart,
  Palette,
  CreditCard,
  Globe,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { useHasModule } from '@/hooks/useOrganizationModules'
import { useBranding } from '@/contexts/BrandingContext'
import { useSubscription } from '@/hooks/useSubscription'
import { Sparkles } from 'lucide-react'

const navigation = [
  {
    title: 'Core',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Documents', href: '/documents', icon: FileText },
    ],
  },
  {
    title: 'Compliance',
    items: [
      { name: 'Products', href: '/products', icon: Package, module: 'core-products' },
      { name: 'Components', href: '/components', icon: Cpu, module: 'certificate-manager' },
      { name: 'Checklists', href: '/checklists', icon: ClipboardCheck, module: 'core-checklists' },
      { name: 'Suppliers', href: '/suppliers', icon: Truck, module: 'supplier-management' },
      { name: 'Supply Chain', href: '/supply-chain', icon: Network, module: 'supplier-management' },
      { name: 'Markets', href: '/markets', icon: Globe, module: 'core-products' },
    ],
  },
  {
    title: 'Quality Management',
    items: [
      { name: 'QMS Dashboard', href: '/qms', icon: ShieldCheck, module: 'qms' },
      { name: 'MRM', href: '/qms/mrm', icon: Calendar, module: 'qms' },
      { name: 'Actions', href: '/qms/actions', icon: ListTodo, module: 'qms' },
      { name: 'Objectives', href: '/qms/objectives', icon: Target, module: 'qms' },
      { name: 'Metrics', href: '/qms/metrics', icon: BarChart3, module: 'qms' },
      { name: 'External Refs', href: '/qms/references', icon: Link2, module: 'qms' },
      { name: 'Internal Audits', href: '/qms/audits', icon: ClipboardList, module: 'qms' },
      { name: 'External Audits', href: '/qms/external-audits', icon: Award, module: 'qms' },
      { name: 'Reports', href: '/reports', icon: FileBarChart, module: 'qms' },
    ],
  },
  {
    title: 'Organization',
    items: [
      { name: 'Team', href: '/team', icon: Users },
      { name: 'Company', href: '/company', icon: Building2 },
      { name: 'Branding', href: '/settings/branding', icon: Palette },
      { name: 'Billing', href: '/billing', icon: CreditCard },
    ],
  },
  {
    title: 'Support',
    items: [
      { name: 'Settings', href: '/settings', icon: Settings },
      { name: 'Help', href: '/help', icon: HelpCircle },
    ],
  },
]

interface NavItemConfig {
  name: string
  href: string
  icon: React.ElementType
  module?: string
}

function NavItem({ item, onNavigate }: { item: NavItemConfig; onNavigate?: () => void }) {
  const location = useLocation()
  const { hasModule, isLoading } = useHasModule(item.module || '')

  const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/')
  const isLocked = item.module && !hasModule && !isLoading

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-certify-100 text-certify-700'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        isLocked && 'opacity-60'
      )}
    >
      <item.icon className="h-4 w-4" />
      <span className="flex-1">{item.name}</span>
      {isLocked && (
        <Badge variant="secondary" className="text-xs gap-1 bg-blue-100 text-blue-700">
          <Lock className="h-3 w-3" />
          Pro
        </Badge>
      )}
    </Link>
  )
}

const TIER_LABELS: Record<string, string> = {
  free_trial: 'Free Trial',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
}

function SidebarPlanBadge() {
  const { planTier, isTrial, trialDaysRemaining, isTrialExpired, loading } = useSubscription()

  if (loading) return null

  const tierLabel = TIER_LABELS[planTier] || 'Free'

  if (isTrial && !isTrialExpired) {
    return (
      <Link to="/billing" className="block">
        <div className="rounded-lg border border-certify-200 bg-certify-50 p-3">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-certify-600" />
            <span className="text-xs font-semibold text-certify-700">Free Trial</span>
          </div>
          <p className="mt-1 text-xs text-certify-600">
            {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">Full Professional access</p>
        </div>
      </Link>
    )
  }

  if (isTrialExpired) {
    return (
      <Link to="/pricing" className="block">
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <span className="text-xs font-semibold text-red-700">Trial Expired</span>
          <p className="mt-1 text-xs text-red-600">Subscribe to continue</p>
        </div>
      </Link>
    )
  }

  return (
    <Link to="/billing" className="block">
      <div className="rounded-lg border bg-muted/50 p-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-foreground">{tierLabel}</span>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Active</Badge>
        </div>
      </div>
    </Link>
  )
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { branding } = useBranding()

  return (
    <div className="flex h-full flex-col border-r bg-background">
      {/* Logo for mobile sheet */}
      <div className="flex h-14 items-center border-b px-4 md:hidden">
        <Link to="/dashboard" className="flex items-center gap-2">
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.companyName}
              className="h-8 w-auto"
            />
          ) : (
            <span className="font-semibold text-lg">{branding.companyName}</span>
          )}
        </Link>
      </div>

      <ScrollArea className="flex-1 py-4">
        <nav className="grid gap-1 px-2">
          {navigation.map((section) => (
            <div key={section.title} className="py-2">
              <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {section.title}
              </h3>
              {section.items.map((item) => (
                <NavItem key={item.name} item={item} onNavigate={onNavigate} />
              ))}
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t p-4 space-y-3">
        <SidebarPlanBadge />
        <div className="rounded-lg bg-certify-50 p-3">
          <p className="text-xs font-medium text-certify-700">Need help?</p>
          <p className="mt-1 text-xs text-certify-600">
            Contact support or check our documentation
          </p>
          <Link
            to="/help"
            onClick={onNavigate}
            className="mt-2 inline-block text-xs font-medium text-certify-700 underline-offset-4 hover:underline"
          >
            Get support
          </Link>
        </div>
      </div>
    </div>
  )
}
